/**
 * SENVÈ Labor Day offer slider — phone-only (<=749px) infinite carousel.
 *
 * Progressive enhancement: the CSS already makes `.senve-ld-edit__grid` a
 * native scroll-snap track on mobile, so with JS off the user still gets a
 * swipeable (finite) 3-card carousel. This element adds the seamless
 * infinite loop + wires the 3 pagination dots (which are rendered in
 * Liquid and always laid out, so revealing them causes no layout shift).
 *
 * Loop technique: clone the 3 real cards once before and once after
 * (9 slides), start centred on the real Bundle slide, and once scrolling
 * SETTLES (position stable, no active pointer) on a clone, teleport
 * instantly to the matching real slide. A full clone-set on each side
 * absorbs fast flings; a very fast multi-slide fling can still briefly
 * touch a physical edge before the settle-teleport corrects it.
 *
 * Clones are inert / aria-hidden / href- and id-stripped — only the 3
 * real <a> cards are links, focusable, or announced. LTR only (the store
 * has no RTL locales).
 *
 * Tablet + desktop (>=750px): this element does nothing; the CSS grid
 * (the section's original static 3-up layout) is untouched.
 */
(() => {
  if (customElements.get('ld-offer-slider')) return;

  // 749px matches the site's own mobile/tablet split (.page-width switches
  // at min-width:750px in base.css) — the carousel is phone-only; tablet
  // and desktop both get the static 3-up grid.
  var MOBILE = '(max-width: 749px)';
  var SETTLE_MS = 160;
  var RECHECK_MS = 120;
  var SCROLL_IDLE_MS = 450;

  class LdOfferSlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-ld-track]');
      this.dotsWrap = this.querySelector('[data-ld-dots]');
      if (!this.track) return;
      this.mq = window.matchMedia(MOBILE);
      this._onMq = this.syncBreakpoint.bind(this);
      this.mq.addEventListener('change', this._onMq);
      this._onResize = () => {
        clearTimeout(this._resizeT);
        this._resizeT = setTimeout(this._onMq, 150);
      };
      window.addEventListener('resize', this._onResize);
      this.syncBreakpoint();
    }

    disconnectedCallback() {
      if (this.mq) this.mq.removeEventListener('change', this._onMq);
      window.removeEventListener('resize', this._onResize);
      clearTimeout(this._resizeT);
      this.teardown();
    }

    syncBreakpoint() {
      if (this.mq.matches) this.build();
      else this.teardown();
    }

    build() {
      if (this.built) return;
      this.reals = Array.prototype.slice.call(
        this.track.querySelectorAll('.senve-ld-edit__card:not([data-ld-clone])')
      );
      if (this.reals.length < 2) return;
      this.count = this.reals.length;

      var before = document.createDocumentFragment();
      var after = document.createDocumentFragment();
      this.reals.forEach((card) => {
        before.appendChild(this.makeClone(card));
        after.appendChild(this.makeClone(card));
      });
      this.track.insertBefore(before, this.reals[0]);
      this.track.appendChild(after);
      this.slides = Array.prototype.slice.call(this.track.querySelectorAll('.senve-ld-edit__card'));

      this.dots = this.dotsWrap
        ? Array.prototype.slice.call(this.dotsWrap.querySelectorAll('[data-ld-dot]'))
        : [];
      this.dots.forEach((dot, i) => {
        dot._onClick = () => this.goTo(i);
        dot.addEventListener('click', dot._onClick);
      });

      this._startReal = Math.min(1, this.count - 1); // Bundle
      this._activeReal = this._startReal;
      this._raf = 0;
      this._settleTimer = 0;
      this._pointerDown = false;
      this._animating = false;
      this._lastScrollTs = 0;
      this._lastScrollLeft = -1;
      this._lastWidth = this.track.clientWidth;

      this._onScroll = this.onScroll.bind(this);
      this._onSettle = this.onSettle.bind(this);
      this._onDown = () => { this._pointerDown = true; };
      this._onUp = () => { this._pointerDown = false; this.queueSettle(); };
      this.track.addEventListener('scroll', this._onScroll, { passive: true });
      this.track.addEventListener('scrollend', this._onSettle);
      this.track.addEventListener('pointerdown', this._onDown, { passive: true });
      window.addEventListener('pointerup', this._onUp, { passive: true });
      window.addEventListener('pointercancel', this._onUp, { passive: true });
      window.addEventListener('touchend', this._onUp, { passive: true });
      window.addEventListener('blur', this._onUp);

      this._ro = new ResizeObserver(() => {
        if (!this.built) return;
        var w = this.track.clientWidth;
        if (w === this._lastWidth) return;             // height-only change
        this._lastWidth = w;
        if (this._pointerDown) return;
        if (Date.now() - this._lastScrollTs < SCROLL_IDLE_MS) return; // mid-fling
        this.setScroll(this.count + this._activeReal);
      });
      this._ro.observe(this.track);

      this.built = true;
      this.setAttribute('data-ld-initialized', '');
      this.setScroll(this.count + this._startReal);   // synchronous
      this.updateDots(this._startReal);
    }

    teardown() {
      if (!this.built) return;
      this.track.removeEventListener('scroll', this._onScroll);
      this.track.removeEventListener('scrollend', this._onSettle);
      this.track.removeEventListener('pointerdown', this._onDown);
      window.removeEventListener('pointerup', this._onUp);
      window.removeEventListener('pointercancel', this._onUp);
      window.removeEventListener('touchend', this._onUp);
      window.removeEventListener('blur', this._onUp);
      if (this._ro) this._ro.disconnect();
      if (this._raf) cancelAnimationFrame(this._raf);
      if (this._settleTimer) clearTimeout(this._settleTimer);
      this.track.querySelectorAll('[data-ld-clone]').forEach((n) => n.remove());
      this.dots.forEach((dot) => dot.removeEventListener('click', dot._onClick));
      this.dots.forEach((dot) => { dot.classList.remove('is-active'); dot.removeAttribute('aria-current'); });
      this.track.style.removeProperty('scroll-snap-type');
      this.track.style.removeProperty('scroll-behavior');
      this.setScroll(0);
      this.built = false;
      this.removeAttribute('data-ld-initialized');
    }

    makeClone(card) {
      var clone = card.cloneNode(true);
      clone.setAttribute('data-ld-clone', '');
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('inert', '');
      clone.removeAttribute('href');
      clone.removeAttribute('id');
      clone.tabIndex = -1;
      clone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
      clone.querySelectorAll('a, button, [tabindex], input, select, textarea').forEach((el) => {
        el.tabIndex = -1;
        el.setAttribute('aria-hidden', 'true');
      });
      return clone;
    }

    snapLeft(i) {
      var s = this.slides[i];
      return s.offsetLeft - (this.track.clientWidth - s.clientWidth) / 2;
    }

    currentIndex() {
      var centre = this.track.scrollLeft + this.track.clientWidth / 2;
      var best = 0;
      var bestDist = Infinity;
      for (var i = 0; i < this.slides.length; i++) {
        var s = this.slides[i];
        var d = Math.abs(s.offsetLeft + s.clientWidth / 2 - centre);
        if (d < bestDist) { bestDist = d; best = i; }
      }
      return best;
    }

    // Instant scroll to a slide index. Neutralises both the CSS
    // `scroll-behavior: smooth` and mandatory snap for the assignment,
    // then restores them (the destination is already a valid snap point).
    setScroll(index) {
      var prevSnap = this.track.style.scrollSnapType;
      this.track.style.scrollBehavior = 'auto';
      this.track.style.scrollSnapType = 'none';
      this.track.scrollTo({ left: this.snapLeft(index), behavior: 'auto' });
      this.track.style.scrollSnapType = prevSnap || '';
      this.track.style.removeProperty('scroll-behavior');
    }

    onScroll() {
      this._lastScrollTs = Date.now();
      if (!this._animating && !this._raf) {
        this._raf = requestAnimationFrame(() => {
          this._raf = 0;
          if (!this._animating) this.updateDots(this.realOf(this.currentIndex()));
        });
      }
      this.queueSettle();
    }

    queueSettle() {
      if (this._settleTimer) clearTimeout(this._settleTimer);
      this._settleTimer = setTimeout(this._onSettle, SETTLE_MS);
    }

    onSettle() {
      if (this._settleTimer) { clearTimeout(this._settleTimer); this._settleTimer = 0; }
      if (this._pointerDown || !this.built) return;
      // Require the position to be stable across two samples before we
      // teleport — guards against momentum that briefly pauses.
      var now = Math.round(this.track.scrollLeft);
      if (now !== this._lastScrollLeft) {
        this._lastScrollLeft = now;
        this._settleTimer = setTimeout(this._onSettle, RECHECK_MS);
        return;
      }
      this._animating = false;
      var idx = this.currentIndex();
      var real = this.realOf(idx);
      this._activeReal = real;
      var canonical = this.count + real;
      if (idx !== canonical) this.setScroll(canonical);
      this.updateDots(real);
    }

    goTo(realIndex) {
      this._activeReal = realIndex;
      this._animating = true;
      var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.track.scrollTo({
        left: this.snapLeft(this.count + realIndex),
        behavior: reduce ? 'auto' : 'smooth',
      });
      this.updateDots(realIndex);
      this.queueSettle();
    }

    realOf(index) {
      return ((index % this.count) + this.count) % this.count;
    }

    updateDots(real) {
      this.dots.forEach((dot, i) => {
        var on = i === real;
        dot.classList.toggle('is-active', on);
        dot.setAttribute('aria-current', on ? 'true' : 'false');
      });
    }
  }

  customElements.define('ld-offer-slider', LdOfferSlider);
})();
